/**
 * feat-307-redesign-tenant-mgmt — the three deep-linkable levels of
 * /tenant_management (spec D1).
 *
 *   /tenant_management                                   root
 *   /tenant_management/:tenantId                         tenant pane
 *   /tenant_management/:tenantId/locations/:locationId   location pane
 *
 * The dashboard parses the CURRENT PATHNAME rather than calling `useParams()`.
 * App.tsx declares each level as its own <Route> (all behind StaffRoute), and
 * parsing the pathname means the same component also works when it is mounted
 * without a matching <Route> (the unit tests mount it in a bare MemoryRouter).
 *
 * `/tenant_management/demo-setup` is a separate, static route declared in
 * App.tsx; react-router ranks a static segment above `:tenantId`, so it keeps
 * winning. `parseManagementPath` also refuses it, as a second line.
 */
import { matchPath } from 'react-router-dom';

export const TENANT_MANAGEMENT_ROOT = '/tenant_management';
export const TENANT_ROUTE_PATTERN = '/tenant_management/:tenantId';
export const LOCATION_ROUTE_PATTERN = '/tenant_management/:tenantId/locations/:locationId';

/** Static children of /tenant_management that are NOT tenant ids. */
const RESERVED_SEGMENTS = new Set(['demo-setup']);

export interface ManagementSelection {
  tenantId: string | null;
  locationId: string | null;
}

const decode = (segment: string) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

export function parseManagementPath(pathname: string): ManagementSelection {
  const loc = matchPath({ path: LOCATION_ROUTE_PATTERN, end: true }, pathname);
  if (loc?.params.tenantId && loc.params.locationId && !RESERVED_SEGMENTS.has(loc.params.tenantId)) {
    return { tenantId: decode(loc.params.tenantId), locationId: decode(loc.params.locationId) };
  }
  const ten = matchPath({ path: TENANT_ROUTE_PATTERN, end: true }, pathname);
  if (ten?.params.tenantId && !RESERVED_SEGMENTS.has(ten.params.tenantId)) {
    return { tenantId: decode(ten.params.tenantId), locationId: null };
  }
  return { tenantId: null, locationId: null };
}

export const rootPath = () => TENANT_MANAGEMENT_ROOT;
export const tenantPath = (tenantId: string) =>
  `${TENANT_MANAGEMENT_ROOT}/${encodeURIComponent(tenantId)}`;
export const locationPath = (tenantId: string, locationId: string) =>
  `${tenantPath(tenantId)}/locations/${encodeURIComponent(locationId)}`;
